import React, { useCallback, useEffect, useState } from 'react';
import { supabase, SUPABASE_ENV } from './supabase';
import { isPlatformAdmin } from './api';
import Matrix from './Matrix';
import Reporting from './Reporting';
import VisionTraining from './VisionTraining';
import AiReports from './AiReports';
import FeatureRequests from './FeatureRequests';
import FeatureIdeas from './FeatureIdeas';
import Admins from './Admins';
import Invite from './Invite';
import ControlPlane from './ControlPlane';
import RatePlans from './RatePlans';
import Collaborators from './Collaborators';
import EsgFactors from './EsgFactors';

type Tab = 'control-plane' | 'pricing' | 'matrix' | 'esg' | 'reporting' | 'collaborators' | 'vision' | 'ai-reports' | 'feature-requests' | 'ideas' | 'invite' | 'admins';

/**
 * Compact environment badge. Says only 'local' or 'cloud' — never the raw
 * project URL. Cloud reads as an amber "production" signal so an admin knows
 * they are about to touch live data; local stays neutral.
 */
function EnvPill({ target }: { target: 'local' | 'cloud' }) {
  const isCloud = target === 'cloud';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderRadius: 999,
        padding: '2px 9px',
        border: '1px solid',
        borderColor: isCloud
          ? 'color-mix(in srgb, var(--accent) 45%, transparent)'
          : 'var(--border-default)',
        color: isCloud ? 'var(--warn)' : 'var(--muted)',
        background: isCloud ? 'var(--warn-subtle)' : 'var(--panel2)',
      }}
    >
      {isCloud ? 'Cloud' : 'Local'}
    </span>
  );
}

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    setBusy(true);
    setErr(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setErr(error.message);
    setBusy(false);
  };

  return (
    <div className="wrap" style={{ maxWidth: 380 }}>
      <div className="card" style={{ marginTop: 60 }}>
        <h1>Platform Admin</h1>
        <p className="sub" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>Sign in with a platform-admin account.</span>
          <EnvPill target={SUPABASE_ENV.target} />
        </p>
        <label>Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
        <label>Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void signIn()} />
        {err ? <p style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</p> : null}
        <div style={{ marginTop: 14 }}>
          <button className="btn primary" onClick={() => void signIn()} disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [admin, setAdmin] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>('control-plane');
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const toast = useCallback((m: string) => {
    setToastMsg(m);
    setTimeout(() => setToastMsg(null), 2600);
  }, []);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSignedIn(!!data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      // Only track sign-in/out here. Do NOT reset `admin` on every event
      // (e.g. TOKEN_REFRESHED) - the admin check keys on `signedIn`, so
      // resetting without a re-trigger would hang on "Checking access…".
      setSignedIn(!!session);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!signedIn) {
      setAdmin(null);
      return;
    }
    void isPlatformAdmin().then(setAdmin);
  }, [signedIn]);

  if (!ready) return <div className="wrap muted">Loading…</div>;
  if (!signedIn) return <Login />;
  if (admin === null) return <div className="wrap muted">Checking access…</div>;
  if (!admin)
    return (
      <div className="wrap" style={{ maxWidth: 460 }}>
        <div className="card" style={{ marginTop: 60 }}>
          <h1>Not authorized</h1>
          <p className="sub">This account is not a platform administrator.</p>
          <button className="btn" onClick={() => void supabase.auth.signOut()}>
            Sign out
          </button>
        </div>
      </div>
    );

  return (
    <div>
      <div className="topbar">
        <span className="brand">AssetWRX</span>
        <span className="tag">Platform Admin</span>
        <div className="nav">
          <button className={tab === 'control-plane' ? 'active' : ''} onClick={() => setTab('control-plane')}>
            Workspaces
          </button>
          <button className={tab === 'pricing' ? 'active' : ''} onClick={() => setTab('pricing')}>
            Pricing
          </button>
          <button className={tab === 'matrix' ? 'active' : ''} onClick={() => setTab('matrix')}>
            Edition matrix
          </button>
          <button className={tab === 'esg' ? 'active' : ''} onClick={() => setTab('esg')}>
            ESG factors
          </button>
          <button className={tab === 'reporting' ? 'active' : ''} onClick={() => setTab('reporting')}>
            Reporting
          </button>
          <button className={tab === 'collaborators' ? 'active' : ''} onClick={() => setTab('collaborators')}>
            Collaborators
          </button>
          <button className={tab === 'vision' ? 'active' : ''} onClick={() => setTab('vision')}>
            Vision training
          </button>
          <button className={tab === 'ai-reports' ? 'active' : ''} onClick={() => setTab('ai-reports')}>
            AI reports
          </button>
          <button className={tab === 'feature-requests' ? 'active' : ''} onClick={() => setTab('feature-requests')}>
            Feature requests
          </button>
          <button className={tab === 'ideas' ? 'active' : ''} onClick={() => setTab('ideas')}>
            Ideas
          </button>
          <button className={tab === 'invite' ? 'active' : ''} onClick={() => setTab('invite')}>
            Invite
          </button>
          <button className={tab === 'admins' ? 'active' : ''} onClick={() => setTab('admins')}>
            Admins
          </button>
          <button onClick={() => void supabase.auth.signOut()}>Sign out</button>
        </div>
      </div>
      <div className="wrap">
        {tab === 'control-plane' ? (
          <>
            <h1>Workspaces</h1>
            <p className="sub">
              What each workspace is paying, and exactly which capabilities resolve on for it — the
              winning source, any blocking dependency, and the revision the application is consuming.
            </p>
            <ControlPlane toast={toast} />
          </>
        ) : tab === 'pricing' ? (
          <>
            <h1>Pricing</h1>
            <RatePlans toast={toast} />
          </>
        ) : tab === 'matrix' ? (
          <>
            <h1>What each plan includes</h1>
            <p className="sub">
              Rows are Modules → Features; tick a cell to include a feature in a plan. Changes save
              immediately and apply to every workspace on that plan. This is what a plan
              <em> gets</em>; what a plan <em>costs</em> is under Pricing.
            </p>
            <Matrix toast={toast} />
          </>
        ) : tab === 'esg' ? (
          <>
            <h1>ESG factors</h1>
            <p className="sub">The carbon factors and refrigerant GWPs behind every workspace's ESG figures. Prices, plans and trial are on Pricing and Matrix.</p>
            <EsgFactors toast={toast} />
          </>
        ) : tab === 'reporting' ? (
          <>
            <h1>Reporting</h1>
            <p className="sub">Edition mix, configuration/adoption, and runtime usage.</p>
            <Reporting />
          </>
        ) : tab === 'collaborators' ? (
          <>
            <h1>Collaborators</h1>
            <p className="sub">
              Contractors, requesters and report reviewers who signed up through an invite to work: who
              brought them, what they do, and who has gone on to run a workspace of their own.
            </p>
            <Collaborators toast={toast} />
          </>
        ) : tab === 'vision' ? (
          <>
            <h1>Vision training</h1>
            <p className="sub">
              Opted-in review decisions become training data weekly; a model trains at most once a month when there is
              enough; it is benchmarked against what is live; nothing goes live until you release it.
            </p>
            <VisionTraining toast={toast} />
          </>
        ) : tab === 'ai-reports' ? (
          <>
            <h1>AI reports</h1>
            <p className="sub">AI output people reported from the app, with the text they were shown.</p>
            <AiReports toast={toast} />
          </>
        ) : tab === 'feature-requests' ? (
          <>
            <h1>Feature requests</h1>
            <p className="sub">
              What people asked for from the app. Set a status or reply and the sender gets a notification; they
              see it under Help &amp; support, Your requests. A morning email lists new requests.
            </p>
            <FeatureRequests toast={toast} />
          </>
        ) : tab === 'ideas' ? (
          <>
            <h1>Ideas</h1>
            <p className="sub">
              The board people vote on in the app. Write each idea in your own words; only published ideas show,
              and voters hear when an idea's status changes.
            </p>
            <FeatureIdeas toast={toast} />
          </>
        ) : tab === 'invite' ? (
          <>
            <h1>Invite</h1>
            <p className="sub">Bring a new customer in while public sign-up is closed.</p>
            <Invite toast={toast} />
          </>
        ) : (
          <>
            <h1>Admins</h1>
            <p className="sub">Who can open this portal. Add or remove platform administrators.</p>
            <Admins toast={toast} />
          </>
        )}
      </div>
      {toastMsg ? <div className="toast">{toastMsg}</div> : null}
    </div>
  );
}
